import { Component, inject, signal, computed, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSuggestionsService } from '../../../../core/services/mat-suggestions.service';
import { MatSuggestion } from '../../../../core/models/mat-suggestion.model';
import { MatDashboardService } from '../../../../core/services/mat-dashboard.service';
import { MatSummary } from '../../../../core/models/mat-dashboard.model';
import { AuthService } from '../../../../core/services/auth.service';
import { catchError, of } from 'rxjs';

const ANA_AVATAR_URL = 'assets/icons/ana-avatar.jpg';

@Component({
  selector: 'app-ana-assistant',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule],
  templateUrl: './ana-assistant.html',
  styleUrl: './ana-assistant.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AnaAssistantComponent implements OnInit, OnDestroy {
  private readonly suggestionsService = inject(MatSuggestionsService);
  private readonly matDashboardService = inject(MatDashboardService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly avatarUrl = ANA_AVATAR_URL;
  readonly isExpanded = signal<boolean>(false);
  readonly showPreview = signal<boolean>(false);
  readonly summary = signal<MatSummary | null>(null);

  readonly isHospitalUser = this.authService.isHospitalUser;
  readonly hasMatAccess = this.authService.hasMatAccess;
  readonly suggestions = this.suggestionsService.suggestions;
  readonly isLoading = this.suggestionsService.isLoading;
  readonly unreadCount = this.suggestionsService.unreadCount;
  readonly hasSuggestions = this.suggestionsService.hasSuggestions;
  readonly totalCount = this.suggestionsService.totalCount;

  readonly hasMatData = computed(() => {
    const s = this.summary();
    return s !== null && (s.monitoredPatients ?? 0) > 0;
  });

  readonly monitoredCount = computed(() => this.summary()?.monitoredPatients ?? 0);
  readonly atRiskCount = computed(() => this.summary()?.atRiskPatients ?? 0);

  readonly redSuggestions = computed(() => this.suggestions().filter(s => s.severity === 'red'));
  readonly yellowSuggestions = computed(() => this.suggestions().filter(s => s.severity === 'yellow'));
  readonly greenSuggestions = computed(() => this.suggestions().filter(s => s.severity === 'green'));

  ngOnInit(): void {
    if (!this.hasMatAccess()) return;
    this.suggestionsService.startPolling();
    this.matDashboardService.getSummary().pipe(
      catchError(() => of(null))
    ).subscribe(s => {
      if (s) this.summary.set(s);
    });
  }

  ngOnDestroy(): void {
    this.suggestionsService.stopPolling();
  }

  onToggle(): void {
    if (this.isExpanded()) {
      this.isExpanded.set(false);
    } else {
      this.isExpanded.set(true);
      this.showPreview.set(false);
      this.suggestionsService.markAsRead();
    }
  }

  onMinimize(): void {
    this.isExpanded.set(false);
  }

  onViewDetails(): void {
    this.isExpanded.set(false);
    this.router.navigate(['/mat']);
  }

  onMatLinkClick(): void {
    this.isExpanded.set(false);
  }

  onDeeplinkClick(suggestion: MatSuggestion): void {
    this.isExpanded.set(false);

    // Medication-specific suggestions default to a /chronic-medications/{id} deeplink,
    // but from Ana we want to land on the patient's MAT view (which lists the medication
    // in context alongside the rest of the patient's monitored medications) rather than
    // the standalone medication detail page. Redirect whenever the suggestion is tied to
    // a specific medication and we know its patient.
    if (suggestion.deeplink.startsWith('/chronic-medications/') && suggestion.patientId) {
      this.router.navigate(['/patients', suggestion.patientId, 'mat']);
      return;
    }

    const [path, query] = suggestion.deeplink.split('?');
    const queryParams: Record<string, string> = {};
    if (query) {
      new URLSearchParams(query).forEach((value, key) => {
        queryParams[key] = value;
      });
    }
    this.router.navigate([path], { queryParams });
  }

  getSeverityIcon(severity: string): string {
    switch (severity) {
      case 'red': return 'warning';
      case 'yellow': return 'schedule';
      case 'green': return 'lightbulb';
      default: return 'info';
    }
  }
}
